import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { api, ApiError, type Person } from '@/api/client';
import { Secret } from '@/components/Secret';
import { ago, romeDay } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { useShell } from '@/lib/shell';

// Who can sign in (D-09): accounts made by an admin, with a password shown once that each person replaces at their
// first sign-in (D-29). The last admin stays an admin; nobody removes themselves.
export function Team(): ReactNode {
    const { user, say } = useShell();
    const queryClient = useQueryClient();
    const people = useQuery({ queryKey: ['people'], queryFn: api.people });
    const now = useNow(30_000);
    const [secret, setSecret] = useState<{ title: string; password: string; note: string } | null>(null);
    const [adding, setAdding] = useState(false);
    const [confirming, setConfirming] = useState<string | null>(null);
    const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['people'] });
    const fail = (error: unknown): void => say(error instanceof ApiError ? error.message : 'The server did not answer');
    const role = useMutation({
        mutationFn: (person: Person) => api.updatePerson(person.id, { role: person.role === 'admin' ? 'member' : 'admin' }),
        onSuccess: refresh,
        onError: fail,
    });
    const reset = useMutation({
        mutationFn: (person: Person) => api.resetPassword(person.id),
        onSuccess: async ({ password }, person) => {
            setSecret({
                title: `A new password for ${person.email}`,
                password,
                note: 'Give it to them once, privately. Their sessions are signed out; they choose their own at the next sign-in.',
            });
            setConfirming(null);
            await refresh();
        },
        onError: fail,
    });
    const remove = useMutation({
        mutationFn: (person: Person) => api.removePerson(person.id),
        onSuccess: async (_, person) => {
            say(`${person.email} can no longer sign in`);
            setConfirming(null);
            await refresh();
        },
        onError: fail,
    });

    return (
        <div className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Team</h1>
                    <p>
                        Who signs in: the developers and the CTO. A new person gets a password shown once, which they replace at their first sign-in.
                    </p>
                </div>
                <button className="act primary" onClick={() => setAdding(!adding)}>
                    {adding ? 'Close' : '+ Add a person'}
                </button>
            </div>
            {secret && <Secret title={secret.title} value={secret.password} onCopy={say} onClose={() => setSecret(null)} note={secret.note} />}
            {adding && (
                <AddPerson
                    onAdded={async (email, password) => {
                        setAdding(false);
                        setSecret({
                            title: `The password of ${email}`,
                            password,
                            note: 'Give it to them once, privately. They choose their own at the first sign-in.',
                        });
                        await refresh();
                    }}
                />
            )}
            <div className="panel">
                <table className="ptable">
                    <thead>
                        <tr className="micro">
                            <th>Person</th>
                            <th>Role</th>
                            <th>Last sign-in</th>
                            <th>Added</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {(people.data?.users ?? []).map((person) => {
                            const key = (what: string): string => `${what}:${person.id}`;
                            const me = person.id === user.id;

                            return (
                                <tr key={person.id}>
                                    <td>
                                        <div className="strong">
                                            {person.name || person.email.split('@')[0]} {me && <span className="micro you">You</span>}
                                        </div>
                                        <div className="mono small sub">{person.email}</div>
                                    </td>
                                    <td>
                                        <button
                                            className={`rolechip ${person.role}`}
                                            title={person.role === 'admin' ? 'Make a member' : 'Make an admin'}
                                            onClick={() => role.mutate(person)}
                                        >
                                            {person.role === 'admin' ? 'Admin' : 'Member'}
                                        </button>
                                    </td>
                                    <td>
                                        {person.last_login_at ? `${ago(Date.parse(person.last_login_at), now)} ago` : 'never'}
                                        {person.must_change_password && <div className="micro">password not chosen yet</div>}
                                    </td>
                                    <td>{romeDay(new Date(person.created_at))}</td>
                                    <td className="row-actions">
                                        <button
                                            className={`act ${confirming === key('reset') ? 'primary' : ''}`}
                                            onClick={() => (confirming === key('reset') ? reset.mutate(person) : setConfirming(key('reset')))}
                                        >
                                            {confirming === key('reset') ? 'Reset now?' : 'Reset password'}
                                        </button>
                                        {!me && (
                                            <button
                                                className={`act ${confirming === key('remove') ? 'danger' : ''}`}
                                                onClick={() => (confirming === key('remove') ? remove.mutate(person) : setConfirming(key('remove')))}
                                            >
                                                {confirming === key('remove') ? 'Remove now?' : 'Remove'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AddPerson(props: { onAdded: (email: string, password: string) => Promise<void> }): ReactNode {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState<'admin' | 'member'>('member');
    const [error, setError] = useState<string | null>(null);
    const add = useMutation({
        mutationFn: () => api.addPerson({ email: email.trim(), name: name.trim(), role }),
        onSuccess: ({ password }) => props.onAdded(email.trim(), password),
        onError: (reason) => setError(reason instanceof ApiError ? reason.message : 'Not added'),
    });

    return (
        <form
            className="panel form-card"
            onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                add.mutate();
            }}
        >
            <div className="panel-head">
                <span className="title">Add a person</span>
                <span className="note">their password is shown once</span>
            </div>
            <div className="form-grid">
                <label>
                    <span className="micro">Email</span>
                    <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus />
                </label>
                <label>
                    <span className="micro">Name</span>
                    <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Optional" maxLength={80} />
                </label>
                <label>
                    <span className="micro">Role</span>
                    <select value={role} onChange={(event) => setRole(event.target.value as 'admin' | 'member')}>
                        <option value="member">member: reads, saves views, handles issues</option>
                        <option value="admin">admin: also sources and people</option>
                    </select>
                </label>
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="dialog-actions">
                <button className="act primary" disabled={add.isPending}>
                    {add.isPending ? 'Adding…' : 'Add'}
                </button>
            </div>
        </form>
    );
}
